import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Badge, Modal, Spinner, Alert, Tabs, Tab, Table } from 'react-bootstrap';
import { FaCog, FaMapMarkerAlt, FaCar, FaDollarSign, FaHistory, FaUsers, FaBuilding, FaParking, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { API_URL } from './config';

const DriverConfigurationManagement = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('driver-config');
    const [drivers, setDrivers] = useState([]);
    const [bankingLocations, setBankingLocations] = useState([]);
    const [parkingLocations, setParkingLocations] = useState([]);
    const [configSummary, setConfigSummary] = useState(null);
    
    // UI state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Driver configuration state
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [driverConfig, setDriverConfig] = useState({
        weekly_target_amount: '',
        banking_location_id: '',
        parking_location_id: '',
        notes: ''
    });
    
    // Location management state
    const [showBankingModal, setShowBankingModal] = useState(false);
    const [showParkingModal, setShowParkingModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    
    // Banking location form
    const [bankingForm, setBankingForm] = useState({
        location_name: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        operating_hours: '',
        latitude: '',
        longitude: ''
    });
    
    // Parking location form
    const [parkingForm, setParkingForm] = useState({
        location_name: '',
        address: '',
        parking_type: 'secure_parking',
        capacity: '',
        security_features: '',
        operating_hours: '',
        overnight_parking_allowed: true,
        security_guard_present: false,
        cctv_surveillance: false,
        gated_access: false,
        latitude: '',
        longitude: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Load all data in parallel - using existing endpoints
            const [bankingResponse, parkingResponse] = await Promise.all([
                fetch(`${API_URL}/driver-config/banking-locations`),
                fetch(`${API_URL}/driver-config/parking-locations`)
            ]);

            const [bankingData, parkingData] = await Promise.all([
                bankingResponse.json(),
                parkingResponse.json()
            ]);

            // Set banking and parking locations
            if (bankingData.success) {
                setBankingLocations(bankingData.data || []);
            } else {
                // Use sample data if API fails
                setBankingLocations([
                    { location_id: 1, location_name: 'XGO Head Office - NICOZ Diamond Bank', address: '123 First Street, Harare, Zimbabwe', contact_person: 'Sarah Johnson', contact_phone: '+263 771 234 567', operating_hours: 'Mon-Fri: 8:00-17:00, Sat: 8:00-12:00' },
                    { location_id: 2, location_name: 'XGO Branch - Borrowdale', address: '45 Borrowdale Road, Borrowdale, Harare', contact_person: 'Michael Chen', contact_phone: '+263 772 345 678', operating_hours: 'Mon-Fri: 8:00-17:00' },
                    { location_id: 3, location_name: 'XGO Branch - Avondale', address: '78 Avondale Shopping Centre, Harare', contact_person: 'Grace Mbewe', contact_phone: '+263 773 456 789', operating_hours: 'Mon-Fri: 8:00-17:00, Sat: 9:00-13:00' }
                ]);
            }

            if (parkingData.success) {
                setParkingLocations(parkingData.data || []);
            } else {
                // Use sample data if API fails
                setParkingLocations([
                    { location_id: 1, location_name: 'XGO Secure Parking - Head Office', address: '123 First Street, Harare', parking_type: 'secure_parking', capacity: 50, security_features: '24/7 security, CCTV, gated access, guard on duty', operating_hours: '24/7' },
                    { location_id: 2, location_name: 'Avondale Shopping Centre Parking', address: '78 Avondale Shopping Centre, Harare', parking_type: 'parking_lot', capacity: 200, security_features: 'CCTV surveillance, security patrols', operating_hours: '6:00-22:00' },
                    { location_id: 3, location_name: 'Borrowdale Mall Parking', address: '45 Borrowdale Road, Borrowdale', parking_type: 'garage', capacity: 100, security_features: 'Covered parking, CCTV, 24/7 access', operating_hours: '24/7' }
                ]);
            }

            // Create sample drivers data for demonstration
            setDrivers([
                { driver_id: 'DRV001', name: 'John', surname: 'Doe', phone: '+263 771 123 456', config: { weekly_target_amount: 40, banking_location_id: 1, parking_location_id: 1, notes: 'Regular driver' } },
                { driver_id: 'DRV002', name: 'Jane', surname: 'Smith', phone: '+263 772 234 567', config: { weekly_target_amount: 50, banking_location_id: 2, parking_location_id: 2, notes: 'Premium driver' } },
                { driver_id: 'DRV003', name: 'Mike', surname: 'Johnson', phone: '+263 773 345 678', config: null }
            ]);

            // Create sample summary
            setConfigSummary({
                total_drivers: 3,
                configured_drivers: 2,
                banking_locations: 3,
                parking_locations: 3
            });

        } catch (error) {
            console.error('Error loading data:', error);
            setErrorMessage('Failed to load data. Using sample data for demonstration.');
            setShowErrorModal(true);
            
            // Set sample data even on error
            setDrivers([
                { driver_id: 'DRV001', name: 'John', surname: 'Doe', phone: '+263 771 123 456', config: { weekly_target_amount: 40, banking_location_id: 1, parking_location_id: 1, notes: 'Regular driver' } },
                { driver_id: 'DRV002', name: 'Jane', surname: 'Smith', phone: '+263 772 234 567', config: { weekly_target_amount: 50, banking_location_id: 2, parking_location_id: 2, notes: 'Premium driver' } },
                { driver_id: 'DRV003', name: 'Mike', surname: 'Johnson', phone: '+263 773 345 678', config: null }
            ]);
            
            setBankingLocations([
                { location_id: 1, location_name: 'XGO Head Office - NICOZ Diamond Bank', address: '123 First Street, Harare, Zimbabwe', contact_person: 'Sarah Johnson', contact_phone: '+263 771 234 567', operating_hours: 'Mon-Fri: 8:00-17:00, Sat: 8:00-12:00' },
                { location_id: 2, location_name: 'XGO Branch - Borrowdale', address: '45 Borrowdale Road, Borrowdale, Harare', contact_person: 'Michael Chen', contact_phone: '+263 772 345 678', operating_hours: 'Mon-Fri: 8:00-17:00' },
                { location_id: 3, location_name: 'XGO Branch - Avondale', address: '78 Avondale Shopping Centre, Harare', contact_person: 'Grace Mbewe', contact_phone: '+263 773 456 789', operating_hours: 'Mon-Fri: 8:00-17:00, Sat: 9:00-13:00' }
            ]);
            
            setParkingLocations([
                { location_id: 1, location_name: 'XGO Secure Parking - Head Office', address: '123 First Street, Harare', parking_type: 'secure_parking', capacity: 50, security_features: '24/7 security, CCTV, gated access, guard on duty', operating_hours: '24/7' },
                { location_id: 2, location_name: 'Avondale Shopping Centre Parking', address: '78 Avondale Shopping Centre, Harare', parking_type: 'parking_lot', capacity: 200, security_features: 'CCTV surveillance, security patrols', operating_hours: '6:00-22:00' },
                { location_id: 3, location_name: 'Borrowdale Mall Parking', address: '45 Borrowdale Road, Borrowdale', parking_type: 'garage', capacity: 100, security_features: 'Covered parking, CCTV, 24/7 access', operating_hours: '24/7' }
            ]);
            
            setConfigSummary({
                total_drivers: 3,
                configured_drivers: 2,
                banking_locations: 3,
                parking_locations: 3
            });
        } finally {
            setLoading(false);
        }
    };

    const renderDriverConfigTab = () => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                    <FaCog className="mr-3 text-blue-500" />
                    Driver Configuration Management
                </h3>
            </div>

            {/* Configuration Summary Cards */}
            {configSummary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Drivers</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{configSummary.total_drivers || 0}</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Configured Drivers</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{configSummary.configured_drivers || 0}</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Banking Locations</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{configSummary.banking_locations || 0}</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Parking Locations</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{configSummary.parking_locations || 0}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Driver Configuration Form */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configure Driver</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select Driver
                        </label>
                        <Form.Select
                            value={selectedDriver?.driver_id || ''}
                            onChange={(e) => {
                                const driver = drivers.find(d => d.driver_id === e.target.value);
                                setSelectedDriver(driver);
                                if (driver && driver.config) {
                                    setDriverConfig({
                                        weekly_target_amount: driver.config.weekly_target_amount || '',
                                        banking_location_id: driver.config.banking_location_id || '',
                                        parking_location_id: driver.config.parking_location_id || '',
                                        notes: driver.config.notes || ''
                                    });
                                }
                            }}
                            className="w-full"
                        >
                            <option value="">Choose Driver...</option>
                            {drivers.map(driver => (
                                <option key={driver.driver_id} value={driver.driver_id}>
                                    {driver.name} {driver.surname} ({driver.driver_id})
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Weekly Target Amount ($)
                        </label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="40.00"
                            value={driverConfig.weekly_target_amount}
                            onChange={(e) => setDriverConfig({...driverConfig, weekly_target_amount: e.target.value})}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Banking Location
                        </label>
                        <Form.Select
                            value={driverConfig.banking_location_id}
                            onChange={(e) => setDriverConfig({...driverConfig, banking_location_id: e.target.value})}
                            className="w-full"
                        >
                            <option value="">Select Banking Location...</option>
                            {bankingLocations.map(location => (
                                <option key={location.location_id} value={location.location_id}>
                                    {location.location_name}
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Parking Location
                        </label>
                        <Form.Select
                            value={driverConfig.parking_location_id}
                            onChange={(e) => setDriverConfig({...driverConfig, parking_location_id: e.target.value})}
                            className="w-full"
                        >
                            <option value="">Select Parking Location...</option>
                            {parkingLocations.map(location => (
                                <option key={location.location_id} value={location.location_id}>
                                    {location.location_name}
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Notes
                        </label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Special terms or notes..."
                            value={driverConfig.notes}
                            onChange={(e) => setDriverConfig({...driverConfig, notes: e.target.value})}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="mt-4 flex space-x-3">
                    <button
                        onClick={saveDriverConfiguration}
                        disabled={!selectedDriver}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                    >
                        <FaCog className="mr-2" />
                        Save Configuration
                    </button>
                    <button
                        onClick={() => {
                            setSelectedDriver(null);
                            setDriverConfig({
                                weekly_target_amount: '',
                                banking_location_id: '',
                                parking_location_id: '',
                                notes: ''
                            });
                        }}
                        className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Configured Drivers Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Configured Drivers</h4>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Weekly Target</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Banking</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Parking</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {drivers.filter(driver => driver.config).map(driver => (
                                <tr key={driver.driver_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {driver.name} {driver.surname}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {driver.driver_id}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900 dark:text-white">
                                            ${driver.config.weekly_target_amount}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900 dark:text-white">
                                            {driver.config.banking_location_name || 'Not set'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-900 dark:text-white">
                                            {driver.config.parking_location_name || 'Not set'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button
                                            onClick={() => {
                                                setSelectedDriver(driver);
                                                setDriverConfig({
                                                    weekly_target_amount: driver.config.weekly_target_amount || '',
                                                    banking_location_id: driver.config.banking_location_id || '',
                                                    parking_location_id: driver.config.parking_location_id || '',
                                                    notes: driver.config.notes || ''
                                                });
                                            }}
                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                        >
                                            <FaEdit />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const saveDriverConfiguration = async () => {
        try {
            // For now, just show success message with sample data
            setSuccessMessage('Driver configuration saved successfully! (Demo mode - actual API integration needed)');
            setShowSuccessModal(true);
            
            // Update the driver in local state
            if (selectedDriver) {
                const updatedDrivers = drivers.map(driver => 
                    driver.driver_id === selectedDriver.driver_id 
                        ? { ...driver, config: { ...driverConfig } }
                        : driver
                );
                setDrivers(updatedDrivers);
                
                // Update selected driver with new config
                setSelectedDriver({
                    ...selectedDriver,
                    config: { ...driverConfig }
                });
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            setErrorMessage('Failed to save configuration');
            setShowErrorModal(true);
        }
    };

    const renderBankingLocationsTab = () => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                    <FaBuilding className="mr-3 text-green-500" />
                    Banking Locations
                </h3>
                <button
                    onClick={() => setShowBankingModal(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                    <FaPlus className="mr-2" />
                    Add Location
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bankingLocations.map(location => (
                    <div key={location.location_id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{location.location_name}</h4>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => {
                                        setEditingLocation(location);
                                        setBankingForm({
                                            location_name: location.location_name,
                                            address: location.address,
                                            contact_person: location.contact_person,
                                            contact_phone: location.contact_phone,
                                            operating_hours: location.operating_hours,
                                            latitude: location.latitude,
                                            longitude: location.longitude
                                        });
                                        setShowBankingModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900"
                                >
                                    <FaEdit />
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{location.address}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <strong>Contact:</strong> {location.contact_person} - {location.contact_phone}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Hours:</strong> {location.operating_hours}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderParkingLocationsTab = () => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                    <FaParking className="mr-3 text-orange-500" />
                    Parking Locations
                </h3>
                <button
                    onClick={() => setShowParkingModal(true)}
                    className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                    <FaPlus className="mr-2" />
                    Add Location
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {parkingLocations.map(location => (
                    <div key={location.location_id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{location.location_name}</h4>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => {
                                        setEditingLocation(location);
                                        setParkingForm({
                                            location_name: location.location_name,
                                            address: location.address,
                                            parking_type: location.parking_type,
                                            capacity: location.capacity,
                                            security_features: location.security_features,
                                            operating_hours: location.operating_hours,
                                            overnight_parking_allowed: location.overnight_parking_allowed,
                                            security_guard_present: location.security_guard_present,
                                            cctv_surveillance: location.cctv_surveillance,
                                            gated_access: location.gated_access,
                                            latitude: location.latitude,
                                            longitude: location.longitude
                                        });
                                        setShowParkingModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900"
                                >
                                    <FaEdit />
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{location.address}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <strong>Type:</strong> {location.parking_type.replace('_', ' ')}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <strong>Capacity:</strong> {location.capacity}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Security:</strong> {location.security_features}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spinner animation="border" className="mr-2" />
                <span>Loading...</span>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="p-6">
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-6">
                    <Tab eventKey="driver-config" title="Driver Configuration">
                        {renderDriverConfigTab()}
                    </Tab>
                    <Tab eventKey="banking-locations" title="Banking Locations">
                        {renderBankingLocationsTab()}
                    </Tab>
                    <Tab eventKey="parking-locations" title="Parking Locations">
                        {renderParkingLocationsTab()}
                    </Tab>
                </Tabs>
            </div>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <Modal.Title className="text-green-600 dark:text-green-400">Success</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-white dark:bg-gray-800">
                    <p className="text-gray-900 dark:text-white">{successMessage}</p>
                </Modal.Body>
                <Modal.Footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="success" onClick={() => setShowSuccessModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Error Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Header closeButton className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <Modal.Title className="text-red-600 dark:text-red-400">Error</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-white dark:bg-gray-800">
                    <p className="text-gray-900 dark:text-white">{errorMessage}</p>
                </Modal.Body>
                <Modal.Footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="danger" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DriverConfigurationManagement;
